import { useCallback } from "react";
import { Badge, Card, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  SendOut,
  SendoutRequestType,
  SendoutStatus,
} from "../../services/sendOuts.service";
import {
  formatSendOutExternalReason,
  stripSendOutHtml,
} from "../../utils/sendOut";
import {
  formatExpectedReturnDate,
  formatSendOutInstantDateTime,
} from "../send-outs/sendOutDatePresentation";

dayjs.extend(relativeTime);

interface ExternalOpinionSendOutCardProps {
  sendOut: SendOut;
  onClick: (sendOutId: string) => void;
}

export function ExternalOpinionSendOutCard({
  sendOut,
  onClick,
}: ExternalOpinionSendOutCardProps) {
  const handleClick = useCallback(() => {
    onClick(sendOut.id);
  }, [onClick, sendOut.id]);

  return (
    <Card
      withBorder
      p="md"
      shadow="xs"
      style={{ cursor: "pointer" }}
      onClick={handleClick}
    >
      <Stack gap={8}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={600} size="sm" lineClamp={1} style={{ flex: 1 }}>
            {sendOut.externalInstitution?.name ?? "Unknown Institution"}
          </Text>
          <Group gap={6} wrap="nowrap">
            <Badge
              size="sm"
              variant="light"
              color={getStatusColor(sendOut.status)}
              style={{ flexShrink: 0 }}
            >
              {getStatusLabel(sendOut.status)}
            </Badge>
            <Tooltip label="View details" withArrow>
              <IconEye
                size={15}
                color="var(--mantine-color-blue-6)"
                style={{ flexShrink: 0 }}
              />
            </Tooltip>
          </Group>
        </Group>

        <Group gap={6} wrap="wrap">
          <Badge size="xs" variant="dot" color="violet">
            {getRequestTypeLabel(sendOut.requestType)}
          </Badge>
          <Text size="xs" c="dimmed">
            &middot; {getMaterialsSummary(sendOut)}
          </Text>
          {sendOut.reportIncluded && (
            <Badge size="xs" variant="outline" color="gray">
              Report
            </Badge>
          )}
          {sendOut.requisitionIncluded && (
            <Badge size="xs" variant="outline" color="gray">
              Requisition
            </Badge>
          )}
        </Group>

        {formatSendOutExternalReason(sendOut) !== "—" && (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {stripSendOutHtml(formatSendOutExternalReason(sendOut))}
          </Text>
        )}

        <Tooltip label={formatSendOutInstantDateTime(sendOut.createdAt)}>
          <Text size="xs" c="dimmed">
            Requested by{" "}
            <Text span c="dark">
              {sendOut.requestedBy?.fullName ?? "Unknown"}
            </Text>{" "}
            &middot; {dayjs(sendOut.createdAt).fromNow()}
          </Text>
        </Tooltip>

        {sendOut.expectedReturnDate && (
          <Text size="xs" c="dimmed">
            Expected return:{" "}
            <Text span c="dark">
              {formatExpectedReturnDate(sendOut.expectedReturnDate)}
            </Text>
          </Text>
        )}

        {sendOut.status === SendoutStatus.REJECTED &&
          sendOut.rejectionReason && (
            <Text size="xs" c="red">
              Rejected: {sendOut.rejectionReason}
            </Text>
          )}
      </Stack>
    </Card>
  );
}

function getStatusLabel(status: SendoutStatus): string {
  switch (status) {
    case SendoutStatus.PENDING_APPROVAL:
      return "Pending Approval";
    case SendoutStatus.REJECTED:
      return "Rejected";
    case SendoutStatus.PENDING_SHIPPING:
      return "Pending Shipping";
    case SendoutStatus.SHIPPED:
      return "Shipped";
    case SendoutStatus.RECEIVED_PARTIALLY:
      return "Partially Received";
    case SendoutStatus.RECEIVED:
      return "Received";
    default:
      return status;
  }
}

function getStatusColor(status: SendoutStatus): string {
  switch (status) {
    case SendoutStatus.PENDING_APPROVAL:
      return "orange";
    case SendoutStatus.REJECTED:
      return "red";
    case SendoutStatus.PENDING_SHIPPING:
      return "yellow";
    case SendoutStatus.SHIPPED:
      return "blue";
    case SendoutStatus.RECEIVED_PARTIALLY:
      return "cyan";
    case SendoutStatus.RECEIVED:
      return "green";
    default:
      return "gray";
  }
}

function getRequestTypeLabel(type: SendoutRequestType): string {
  switch (type) {
    case SendoutRequestType.PHYSICIAN_REQUEST:
      return "Physician Request";
    case SendoutRequestType.EXTERNAL_INSTITUTION:
      return "External Institution";
    case SendoutRequestType.GCP_CONSULT:
      return "GCP Consult";
    case SendoutRequestType.MATERIAL_RETURN:
      return "Material Return";
    default:
      return type;
  }
}

function getMaterialsSummary(sendOut: SendOut): string {
  const slideCount = sendOut.slidesSent?.length ?? 0;
  const blockCount = sendOut.blocksSent?.length ?? 0;
  const parts: string[] = [];
  if (slideCount > 0)
    parts.push(`${slideCount} slide${slideCount !== 1 ? "s" : ""}`);
  if (blockCount > 0)
    parts.push(`${blockCount} block${blockCount !== 1 ? "s" : ""}`);
  return parts.join(", ") || "No materials listed";
}
